import type { FieldPath, FieldPathValue, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { RadioCard, type RadioCardProps } from "@/components/Radio/RadioCard";

interface FormRadioCardProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<RadioCardProps, "onChange" | "checked" | "defaultChecked"> {
  name: TName;
  value: FieldPathValue<TFormValues, TName>;
  rules?: RegisterOptions<TFormValues, TName>;
}

/**
 * Values for radio buttons can only be strings. If you need to use a different type, you have to convert it to a string yourself.
 */
export function FormRadioCard<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({ rules, label, name, ...props }: FormRadioCardProps<TFormValues, TName>): React.ReactNode {
  const { field } = useController<TFormValues, TName>({ name, rules });

  return (
    <RadioCard label={label} {...props} {...field} value={props.value} defaultChecked={field.value === props.value} />
  );
}

type FormRadioCardGroupProps = {
  children: React.ReactNode;
  layout?: "horizontal" | "vertical";
  legend?: React.ReactNode;
};

export function FormRadioCardGroup({
  children,
  layout = "horizontal",
  legend,
}: FormRadioCardGroupProps): React.ReactNode {
  return (
    <fieldset
      className={twJoin(
        "flex max-w-full min-w-0",
        layout === "horizontal" ? "flex-col gap-space-sm sm:flex-row" : "flex-col gap-space-sm",
      )}
    >
      {legend != null && <legend className="sr-only">{legend}</legend>}
      {children}
    </fieldset>
  );
}
